import { useEffect } from 'preact/hooks'

import type {
  CookieCategory,
  CookieRecord,
} from '../../../services/cookieTranslationService/types'

import { getConfiguredCategories } from '#src/assets/core/consentConfig.ts'
import { useBlockPageScroll } from '#src/assets/hooks/useBlockPageScroll.ts'
import { useCookieCategoryState } from '#src/assets/hooks/useCookieCategoryState.ts'
import { useIsMobile } from '#src/assets/hooks/useIsMobile.ts'
import { getPageDeclaredCategories } from '#src/assets/integrations/declaredCategories.ts'
import type { CookieConsentTranslator } from '#src/assets/lib/i18n.ts'
import type { CookieConsentEngine } from '#src/assets/ui/mount/cookieConsentEngine.ts'
import { PreferencesModalBody } from '#src/assets/ui/preferences/components/Body.tsx'
import { PreferencesModalFooter } from '#src/assets/ui/preferences/components/Footer.tsx'
import { PreferencesModalHeader } from '#src/assets/ui/preferences/components/Header.tsx'
import {
  ModalBackdrop,
  ModalPanel,
} from '#src/assets/ui/shared/components/ModalPanel.tsx'
import type { SiteConfig } from '#src/models/site-configs/siteConfigs.ts'

export function PreferencesModal({
  t,
  siteConfig,
  cookiesByCategory,
  engine,
  visible,
  onClose,
  onConsentSaved,
}: {
  t: CookieConsentTranslator
  siteConfig: SiteConfig
  cookiesByCategory: Record<CookieCategory, Array<CookieRecord>>
  engine: CookieConsentEngine
  visible: boolean
  onClose: () => void
  onConsentSaved: () => void
}) {
  const configuredCategories = (() => {
    const categories = getConfiguredCategories(
      cookiesByCategory,
      getPageDeclaredCategories(),
    )
    if (categories.includes('necessary')) {
      return categories
    }

    return ['necessary', ...categories] as CookieCategory[]
  })()

  const { categoryState, setCategoryState, buildInitialCategoryState } =
    useCookieCategoryState({ engine, configuredCategories })
  const isMobile = useIsMobile()

  useBlockPageScroll(visible)

  useEffect(() => {
    if (visible) {
      setCategoryState(
        buildInitialCategoryState({ engine, configuredCategories }),
      )
    }
  }, [visible])

  if (!visible) {
    return null
  }

  const handleSave = (): void => {
    engine.saveCategories(getCategoriesToSave(categoryState))
    onConsentSaved()
    onClose()
  }

  const handleAcceptAll = (): void => {
    engine.acceptAll()
    onConsentSaved()
    onClose()
  }

  const handleRejectAll = (): void => {
    engine.rejectAll()
    onConsentSaved()
    onClose()
  }

  return (
    <ModalBackdrop
      isMobile={isMobile}
      onBackdropClick={event => {
        if (event.target === event.currentTarget) {
          onClose()
        }
      }}
    >
      <ModalPanel
        isMobile={isMobile}
        onClick={event => event.stopPropagation()}
      >
        <PreferencesModalHeader t={t} onClose={onClose} />
        <PreferencesModalBody
          t={t}
          siteConfig={siteConfig}
          cookiesByCategory={cookiesByCategory}
          configuredCategories={configuredCategories}
          categoryState={categoryState}
          setCategoryState={setCategoryState}
        />
        <PreferencesModalFooter
          t={t}
          onAcceptAll={handleAcceptAll}
          onAcceptNecessary={handleRejectAll}
          onSavePreferences={handleSave}
        />
      </ModalPanel>
    </ModalBackdrop>
  )
}

function getCategoriesToSave(categoryState: Record<string, boolean>): string[] {
  return Object.entries(categoryState)
    .filter(([, enabled]) => enabled)
    .map(([category]) => category)
}
